import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import Header from './Header';
import { apiRequest, broadcastAdminUpdate, clearAuthSession } from '../utils/api';
import {
  cacheAccessibleCompanies,
  getActiveCompany,
  getCachedCompanies,
  getCachedCompaniesAge,
  setActiveCompany,
} from '../utils/companyContext';

const CACHE_TTL_MS = 5 * 60 * 1000;

const DashboardLayout = ({ children, title }) => {
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = window.localStorage.getItem('adminSidebarCollapsed');
      if (saved === 'true') return true;
      if (saved === 'false') return false;
      return window.innerWidth < 768;
    }
    return false;
  });
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeCompany, setActiveCompanyState] = useState(() => getActiveCompany());
  const [companies, setCompanies] = useState(() => getCachedCompanies());
  const [admin, setAdmin] = useState(() => {
    try {
      const stored = localStorage.getItem('authAdmin');
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.error('Failed to parse stored admin profile:', error);
      return null;
    }
  });
  const [hasAttemptedRefresh, setHasAttemptedRefresh] = useState(false);

  useEffect(() => {
    const readAdminFromStorage = () => {
      try {
        const stored = localStorage.getItem('authAdmin');
        setAdmin(stored ? JSON.parse(stored) : null);
      } catch (error) {
        console.error('Failed to parse stored admin profile:', error);
        setAdmin(null);
      }
    };

    const handleAdminUpdated = (event) => {
      if (event?.detail !== undefined) {
        setAdmin(event.detail);
        return;
      }
      readAdminFromStorage();
    };

    const readCompanyFromStorage = () => {
      setActiveCompanyState(getActiveCompany());
    };

    window.addEventListener('authAdminUpdated', handleAdminUpdated);
    window.addEventListener('storage', readAdminFromStorage);
    window.addEventListener('storage', readCompanyFromStorage);
    const handleActiveCompanyChanged = (event) => {
      if (event?.detail !== undefined) {
        setActiveCompanyState(event.detail);
        return;
      }
      setActiveCompanyState(getActiveCompany());
    };

    window.addEventListener('activeCompanyChanged', handleActiveCompanyChanged);

    return () => {
      window.removeEventListener('authAdminUpdated', handleAdminUpdated);
      window.removeEventListener('storage', readAdminFromStorage);
      window.removeEventListener('storage', readCompanyFromStorage);
      window.removeEventListener('activeCompanyChanged', handleActiveCompanyChanged);
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const token = localStorage.getItem('authToken');
    const hasMenus = Array.isArray(admin?.menus) && admin.menus.length > 0;

    if (!token || hasMenus || hasAttemptedRefresh) {
      return undefined;
    }

    const refreshAdminProfile = async () => {
      setHasAttemptedRefresh(true);
      try {
        const { data } = await apiRequest('/api/admin/me', {}, navigate);

        if (!isMounted) {
          return;
        }

        const refreshedAdmin = data?.admin ?? null;

        if (!refreshedAdmin) {
          clearAuthSession(navigate);
          return;
        }

        localStorage.setItem('authAdmin', JSON.stringify(refreshedAdmin));
        setAdmin(refreshedAdmin);
        broadcastAdminUpdate(refreshedAdmin);
      } catch (error) {
        if (isMounted) {
          console.error('Failed to refresh admin profile:', error);
        }
      }
    };

    refreshAdminProfile();

    return () => {
      isMounted = false;
    };
  }, [admin, hasAttemptedRefresh, navigate]);

  useEffect(() => {
    let isMounted = true;
    const token = localStorage.getItem('authToken');
    const hasMenus = Array.isArray(admin?.menus) && admin.menus.length > 0;

    if (token && hasMenus && !admin?.menusCachedAt) {
      const nextAdmin = { ...admin, menusCachedAt: Date.now() };
      localStorage.setItem('authAdmin', JSON.stringify(nextAdmin));
      setAdmin(nextAdmin);
      broadcastAdminUpdate(nextAdmin);
      return undefined;
    }

    const hasFreshMenus =
      hasMenus &&
      Date.now() - Number(admin?.menusCachedAt ?? 0) < CACHE_TTL_MS;

    if (!token || hasFreshMenus) {
      return undefined;
    }

    const loadSidebarMenus = async () => {
      try {
        const { data } = await apiRequest('/api/admin/menus/sidebar', {}, navigate);
        if (!isMounted) {
          return;
        }

        const menus = Array.isArray(data?.menus) ? data.menus : [];

        setAdmin((prev) => {
          let base = prev;
          if (!base) {
            try {
              const stored = localStorage.getItem('authAdmin');
              base = stored ? JSON.parse(stored) : null;
            } catch (parseError) {
              console.error('Failed to parse stored admin profile:', parseError);
              base = null;
            }
          }

          const nextAdmin = base ? { ...base, menus, menusCachedAt: Date.now() } : { menus, menusCachedAt: Date.now() };
          localStorage.setItem('authAdmin', JSON.stringify(nextAdmin));
          broadcastAdminUpdate(nextAdmin);
          return nextAdmin;
        });
      } catch (error) {
        if (isMounted) {
          console.error('Failed to load sidebar menus:', error);
        }
      }
    };

    loadSidebarMenus();

    return () => {
      isMounted = false;
    };
  }, [admin?.menus, admin?.menusCachedAt, navigate]);

  useEffect(() => {
    let isMounted = true;

    const token = localStorage.getItem('authToken');
    const hasFreshCompanies = companies.length > 0 && getCachedCompaniesAge() < CACHE_TTL_MS;

    if (!token || hasFreshCompanies) {
      return undefined;
    }

    const loadCompanies = async () => {
      try {
        const { data } = await apiRequest('/api/companies/my-companies', {}, navigate);
        if (!isMounted) {
          return;
        }

        const list = Array.isArray(data?.companies)
          ? data.companies
          : Array.isArray(data)
          ? data
          : [];

        setCompanies(list);
        cacheAccessibleCompanies(list);

        if (list.length === 1 && !getActiveCompany()) {
          setActiveCompany(list[0]);
          window.location.replace('/dashboard');
        }
      } catch (error) {
        console.error('Failed to load accessible companies:', error);
      }
    };

    loadCompanies();

    return () => {
      isMounted = false;
    };
  }, [companies.length, navigate]);

  const location = useLocation();

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const menus = useMemo(() => admin?.menus ?? [], [admin]);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen((prev) => !prev);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const sidebarWrapperClassName = [
    'sidebar-wrapper',
    isMobileMenuOpen ? 'sidebar-wrapper-open' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const handleCompanySwitch = (company) => {
    setActiveCompanyState(company);
    setActiveCompany(company);
    window.location.reload();
  };

  return (
    <div className="dashboard-layout d-flex flex-column flex-md-row">
      <div className={sidebarWrapperClassName}>
        <Sidebar
          collapsed={sidebarCollapsed}
          toggleSidebar={() => setSidebarCollapsed((prev) => { const next = !prev; if (typeof window !== 'undefined' && window.innerWidth >= 768) window.localStorage.setItem('adminSidebarCollapsed', String(next)); return next; })}
          menus={menus}
          isMobileOpen={isMobileMenuOpen}
          onCloseMobile={closeMobileMenu}
        />
      </div>
      {isMobileMenuOpen && (
        <div
          className="sidebar-backdrop d-md-none"
          role="presentation"
          onClick={closeMobileMenu}
        />
      )}
      <div className="dashboard-main flex-grow-1 d-flex flex-column">
        <Header
          title={`${title || 'Grace Super Market'} `}
          admin={admin}
          activeCompany={activeCompany}
          companies={companies}
          onSelectCompany={handleCompanySwitch}
          onToggleSidebar={toggleMobileMenu}
          isSidebarOpen={isMobileMenuOpen}
        />
        <main className="dashboard-content flex-grow-1 bg-light">
          <div className="dashboard-content-inner container-fluid p-4">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
